// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { Category } from "@insite/client-framework/Services/CategoryService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection from "@insite/mobius/AccordionSection";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Select from "@insite/mobius/Select";
import Typography from "@insite/mobius/Typography";
import BrandsList, { linkListStyle } from "@mcmc/App/Components/Category/BrandsList";
import categoryListStyles from "@mcmc/Overrides/Widgets/Category/CategoryList.style";
import StyledBox from "@mcmc/XDL/Components/Common/Box";
import CloudinaryImage from "@mcmc/XDL/Components/Common/CloudinaryImage";
import { transformationsMap } from "@mcmc/XDL/Utilities/cloudinary";
import orderBy from "lodash/orderBy";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { ThemeProps, withTheme } from "styled-components";

const enum fields {
    showImages = "showImages",
    /* No need for this option to match our design
    showOnlyTopLevelCategories = "showOnlyTopLevelCategories",
    */
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showImages]: boolean;
        /* No need for this option to match our design
        [fields.showOnlyTopLevelCategories]: boolean;
        */
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    return {
        categoriesDataView: getCategoriesDataView(state),
        topLevelCategoryIds: getSubCategoryIds(state, emptyGuid),
        isAllCategoriesPage: getCurrentPage(state).type === "CategoryListPage",
    };
};

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;
interface State {
    sortType: string;
    caterogiesAccordionOpened: boolean;
}

const styles = categoryListStyles;

class CategoryList extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            sortType: "",
            caterogiesAccordionOpened: true,
        };
    }

   

    getCategories = (props: Props) => {
        const { topLevelCategoryIds, categoriesDataView } = props;
        if (!categoriesDataView.value) {
            return [];
        }
        return topLevelCategoryIds && categoriesDataView.value.filter(o => topLevelCategoryIds.indexOf(o.id) > -1);
    };

    UNSAFE_componentWillMount(): void {
        const { categoriesDataView, loadCategories } = this.props;
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories();
        }
    }

    getSortedCategories = (categories: Category[] | undefined, sortType: string) => {
        const sortedCategories = orderBy(categories, ["name"], [sortType as "asc" | "desc"]);
        return sortedCategories;
    };

    render() {
        const {
            id,
            categoriesDataView,
            topLevelCategoryIds,
            isAllCategoriesPage,
            fields: { showImages },
        } = this.props;

        if (categoriesDataView.isLoading) {
            return (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            );
        }

        if (!categoriesDataView.value) {
            return null;
        }

        if (!categoriesDataView.value || !topLevelCategoryIds) {
            return null;
        }
        const { sortType, caterogiesAccordionOpened } = this.state;

        const categoriesLeftList = categoriesDataView.value.filter(o => topLevelCategoryIds.indexOf(o.id) > -1 && o.properties.isEventsCategory?.toLowerCase() !== "true");
        const sortedCategories = sortType ? this.getSortedCategories(categoriesLeftList, sortType) : categoriesLeftList;

        return (
            <StyledWrapper {...styles.wrapper}>
                {isAllCategoriesPage && (
                    <StyledWrapper {...styles.leftSection}>
                        <Accordion headingLevel={2}>
                            <AccordionSection
                                title={translate("Categories")}
                                expanded={caterogiesAccordionOpened}
                                onTogglePanel={() => {
                                    this.setState({ caterogiesAccordionOpened: !caterogiesAccordionOpened });
                                }}
                                {...linkListStyle.accordionSection}
                            >
                                <StyledWrapper {...linkListStyle.contentWrapper}>
                                    {categoriesLeftList &&
                                        categoriesLeftList.map(category => (
                                            <Link key={category.id} {...linkListStyle.link} href={category.path}>
                                                {category.shortDescription}
                                            </Link>
                                        ))}
                                </StyledWrapper>
                            </AccordionSection>
                            <BrandsList includeProductsInSubCategories={false} />
                        </Accordion>
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.rightSection}>
                    {isAllCategoriesPage && (
                        <StyledWrapper {...styles.topSectionWrapper}>
                            <StyledWrapper {...styles.titleWrapper}>
                                <Zone contentId={id} zoneName="title" />
                            </StyledWrapper>
                            <StyledWrapper {...styles.selectWrapper}>
                                <Select
                                    label={translate("Sort By")}
                                    labelPosition="left"
                                    onChange={(event: React.FormEvent<HTMLSelectElement>) =>
                                        this.setState({ sortType: event.currentTarget.value })
                                    }
                                    data-test-selector="categoriesSort"
                                    {...styles.categoriesSort}
                                >
                                    <option value="">{translate("Select Option")}</option>
                                    <option value="asc">{translate("Name: A to Z")}</option>
                                    <option value="desc">{translate("Name: Z to A")}</option>
                                </Select>
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                    {sortedCategories && (
                        <StyledWrapper {...styles.categoriesContentWrapper}>
                            {sortedCategories.map(category => (
                                <StyledBox {...styles.styledBox} key={category.id.toString()}>
                                    <Link href={category.path} {...styles.link} aria-label={category.shortDescription}>
                                        {showImages && (
                                            <StyledWrapper {...styles.categoryImageWrapper}>
                                                <CloudinaryImage
                                                    imageTransformations={transformationsMap.product_sm}
                                                    src={category.smallImagePath}
                                                    altText={category.imageAltText}
                                                />
                                            </StyledWrapper>
                                        )}
                                        <StyledWrapper {...styles.categoryNameWrapper}>
                                            <Typography {...styles.categoryName}>
                                                {category.shortDescription}
                                            </Typography>
                                        </StyledWrapper>
                                    </Link>
                                </StyledBox>
                            ))}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

/* No need for this component to match our design
const SubCategoryLinkView = ({ category }: ReturnType<typeof linkMapStateToProps>) => {
    if (!category) {
        return null;
    }

    return (
        <Link href={category.path} {...styles.subCategoryNameLink}>
            {category.shortDescription}
        </Link>
    );
};

const linkMapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

const SubCategoryLink = connect(linkMapStateToProps)(SubCategoryLinkView);
*/

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CategoryList)),
    definition: {
        group: "Categories",
        icon: "list-ul",
        allowedContexts: ["CategoryListPage", "HomePage"],
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            /* No need for this field to match our design
            {
                name: fields.showOnlyTopLevelCategories,
                displayName: "Show only top-level categories",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 2,
            }, */
        ],
    },
};

export default widgetModule;
